import React, { useCallback, useState } from 'react';
import { Badge, Box, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dashboard,
  Folder,
  Settings,
  Event,
  Contacts,
  LockClock,
  PriceChangeOutlined,
  WorkHistory,
  Person,
  Plagiarism,
  LogoutOutlined,
  ContactSupport,
} from '@mui/icons-material';
// COMPONENTS & STYLES
import styles from 'styles/common/layout.module.scss';
import theme from 'styles/mui/theme';
import { DocuSyncIcon } from 'utilities/icons';
import { useNavigate } from 'react-router-dom';
import performClientLogout from 'utilities/clientLogout';
import LogoutModal from 'containers/common/components/LogoutModal';
import SidebarItem from './SidebarItem';

function Sidebar() {
  const {
    logo,
    transactionsCount,
    unreadTransactionalQuestionsCount,
    unreadTasksCount,
    unreadRequestsCount,
    unreadDocuSyncCount,
    unreadContactsCount,
    unreadPasswordRequestsCount,
    unreadDocumentsCount,
    user,
    hasSupportTicketsAccess,
    hasClientMeetingsAccess,
    hasDocuSyncAccess,
    hasDocuCheckAccess,
  } = useSelector(state => state.auth);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const univarsalLogin = localStorage.getItem('universalLogin');

  const handleLogout = useCallback(() => {
    if (univarsalLogin) {
      navigate('/auth/universal-login');
    }
    performClientLogout(dispatch);
  }, []);

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <Box
      className={`${styles.sidebarContainer} sidebarContainer`}
      sx={{ backgroundColor: theme.palette.themeDark.main }}
    >
      <Box className={styles.logo}>
        <img className={styles.styles} src={logo} alt="Logo" />
      </Box>

      <Box className="list-scroll-wrap">
        <List className="sidebar-list pb-4">
          <SidebarItem icon={<Dashboard />} text="Task Vault" path="/" count={unreadTasksCount} />
          <SidebarItem icon={<Folder />} text="Documents" path="/documents" count={unreadDocumentsCount} />
          {(user.has_docsync_access === 1 || hasDocuSyncAccess === 1) && (
            <SidebarItem icon={<DocuSyncIcon />} text="Docu Sync" path="/docu-sync" count={unreadDocuSyncCount} />
          )}
          {(user.has_docucheck_access === 1 || hasDocuCheckAccess === 1) && (
            <SidebarItem icon={<Plagiarism />} text="Docu Check" path="/docu-check" badgeLabel="New" />
          )}
          {(user.has_support_tickets_access === 1 || hasSupportTicketsAccess === 1) && (
            <SidebarItem icon={<Person />} text="Request+" path="/client-requests" count={unreadRequestsCount} />
          )}
          {(user.has_client_meetings_access === 1 || hasClientMeetingsAccess === 1) && (
            <SidebarItem icon={<WorkHistory />} text="Meetings" path="/meetings" badgeLabel="New" />
          )}
          <SidebarItem
            icon={<PriceChangeOutlined />}
            text="Banking ?'s"
            path="/transactions"
            count={transactionsCount}
          />
          <SidebarItem
            icon={<ContactSupport />}
            text="Transactional Questions"
            path="/transactional-questions"
            count={unreadTransactionalQuestionsCount}
          />
          <SidebarItem icon={<Event />} text="Calendar" path="/calendar" />
          <SidebarItem icon={<Contacts />} text="Contacts" path="/contacts" count={unreadContactsCount} />
          <SidebarItem icon={<LockClock />} text="Pending Access Requests" path="/pending-access-requests" count={unreadPasswordRequestsCount} />
          <SidebarItem icon={<Settings />} text="Settings" path="/settings" />
        </List>
      </Box>

      <Box className="navBottomWrap">
        <List className="sidebar-list navbarBottomList py-0">
          <ListItemButton
            className="listItem"
            onClick={handleDialogOpen}
            sx={{
              transition: 'all .35s ease',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2) !important' },
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px', color: 'white' }} className={styles.listItemIcon}>
              <Badge>
                <LogoutOutlined />
              </Badge>
            </ListItemIcon>
            <ListItemText className="d-none d-md-block" primary="Logout" />
          </ListItemButton>
        </List>
      </Box>

      <LogoutModal
        isDialogOpen={isDialogOpen}
        handleDialogClose={handleDialogClose}
        handleLogoutConfirm={handleLogout}
      />
    </Box>
  );
}

export default Sidebar;
